import { useRef } from 'react';
import { PayloadStates } from '@lore/utils';

/**
 * Keeps displaying the last successful non-empty list while the resource is refetching,
 * and exposes a normalized shape for component use.
 *
 * @param {{ state?: string, data?: any[], meta?: any }} resource
 * @returns {{ data: any[], isFetching: boolean, meta: any }}
 */
export default function useStableList(resource) {
  const ref = useRef([]);
  const isFetching = resource && resource.state === PayloadStates.FETCHING;

  const currentPage = Number(resource && resource.meta && resource.meta.page) || 1;

  if (resource && Array.isArray(resource.data)) {
    if (resource.data.length > 0) {
      if (currentPage > 1) {
        // deduplicate items by ID, keeping the most recent version of each item
        const merged = new Map();

        ref.current.forEach((item) => {
          if (item && item.id) merged.set(item.id, item);
        });

        resource.data.forEach((item) => {
          if (item && item.id) merged.set(item.id, item);
        });

        ref.current = Array.from(merged.values());
      } else {
        // First page or reset
        ref.current = resource.data;
      }
    }
  }

  // Once pagination advances past page 1, always prefer the accumulated stable list,
  // so the UI shows all items loaded so far instead of only the most recent page.
  const active = currentPage > 1
    ? ref.current
    : ((resource && resource.data && resource.data.length > 0) || !isFetching)
      ? (resource && resource.data) || []
      : ref.current;

  return {
    data: active,
    isFetching: Boolean(isFetching),
    meta: resource && resource.meta
  };
}
